// src/services/typesafe.js
import { TypeSafeClient, choice, score, noul } from "@typesafe-ai/sdk";

const apiKey = process.env.REACT_APP_TYPESAFE_API_KEY || process.env.TYPESAFE_API_KEY;
const isConfigured = apiKey && apiKey !== "your-typesafe-api-key-here";

let client = null;
if (isConfigured) {
  client = new TypeSafeClient({ apiKey });
}

export const isTypesafeConfigured = () => isConfigured;

/**
 * Score an order for processing priority (0-10 scale)
 * Higher score = higher priority
 */
export const scoreOrderPriority = async (order) => {
  if (!client) {
    return { score: 5, confidence: 0, error: "TypeSafe not configured" };
  }

  const state = {
    order: {
      id: order.id,
      fineWeight: order.fineWeight,
      grossWeight: order.grossWeight,
      amount: order.amount,
      customerType: order.customerType,
      status: order.orderStatus,
      assignedAt: order.assignedAt,
      packets: order.packets,
      items: order.items,
      operatorId: order.operatorId,
      district: order.district,
    },
  };

  const questions = {
    priority: score("How urgent is this order to process?", [
      "Low priority: Small order, flexible timeline, standard customer",
      "Medium priority: Average order size, normal timeline, regular customer",
      "High priority: Large order or premium customer, needs prompt attention",
      "Critical priority: Very large order, VIP customer, or time-sensitive delivery",
      "Urgent priority: Express delivery required, high-value order, or escalation",
    ]),
  };

  try {
    const response = await client.systemOne({ state, questions });
    return {
      score: response.answers.priority.score,
      confidence: response.answers.priority.confidence,
      probabilities: response.answers.priority.probabilities,
    };
  } catch (error) {
    console.error("TypeSafe priority scoring error:", error);
    return { score: 5, confidence: 0, error: error.message };
  }
};

/**
 * Choose the best courier option for an order
 */
export const chooseBestCourier = async (order, courierOptions) => {
  if (!client || !courierOptions?.length) {
    return { choice: courierOptions?.[0]?.courierId, confidence: 0, error: "TypeSafe not configured" };
  }

  const state = {
    order: {
      fineWeight: order.fineWeight,
      grossWeight: order.grossWeight,
      amount: order.amount,
      district: order.district,
      status: order.orderStatus,
    },
    couriers: courierOptions.map((c) => ({
      courierId: c.courierId,
      courierName: c.courierName,
      branchName: c.branchName,
      contactPerson: c.contactPerson,
      contactNumber: c.contactNumber,
      address: c.address,
    })),
  };

  const criteria = {};
  courierOptions.forEach((c) => {
    criteria[c.courierId] = `${c.courierName} - ${c.branchName} (${c.contactPerson}, ${c.contactNumber})`;
  });

  const questions = {
    bestCourier: choice("Which courier is the best choice for this order?", criteria),
  };

  try {
    const response = await client.systemOne({ state, questions });
    const choiceResult = response.answers.bestCourier;
    return {
      choice: choiceResult.choice,
      confidence: choiceResult.confidence,
      probabilities: choiceResult.probabilities,
    };
  } catch (error) {
    console.error("TypeSafe courier selection error:", error);
    return { choice: courierOptions[0].courierId, confidence: 0, error: error.message };
  }
};

/**
 * Score a courier option for a specific order (0-10)
 */
export const scoreCourierOption = async (order, courier) => {
  if (!client) {
    return { score: 5, confidence: 0, error: "TypeSafe not configured" };
  }

  const state = {
    order: {
      fineWeight: order.fineWeight,
      grossWeight: order.grossWeight,
      amount: order.amount,
      district: order.district,
    },
    courier: {
      courierId: courier.courierId,
      courierName: courier.courierName,
      branchName: courier.branchName,
      contactPerson: courier.contactPerson,
      contactNumber: courier.contactNumber,
      address: courier.address,
    },
  };

  const questions = {
    suitability: score("How suitable is this courier for this order?", [
      "Very poor: Wrong district, no capacity, unreliable",
      "Poor: Limited coverage, slow, poor tracking",
      "Below average: Basic service, occasional delays",
      "Average: Standard service, reasonable speed",
      "Good: Reliable, good tracking, fair pricing",
      "Very good: Fast, reliable, good coverage, competitive rates",
      "Excellent: Premium service, fastest, best tracking, preferred partner",
    ]),
  };

  try {
    const response = await client.systemOne({ state, questions });
    return {
      score: response.answers.suitability.score,
      confidence: response.answers.suitability.confidence,
      probabilities: response.answers.suitability.probabilities,
    };
  } catch (error) {
    console.error("TypeSafe courier scoring error:", error);
    return { score: 5, confidence: 0, error: error.message };
  }
};

/**
 * Check if an order looks suspicious/fraudulent (yes/no with confidence)
 */
export const checkOrderFraudRisk = async (order) => {
  if (!client) {
    return { noul: 0.5, confidence: 0, error: "TypeSafe not configured" };
  }

  const state = {
    order: {
      id: order.id,
      fineWeight: order.fineWeight,
      grossWeight: order.grossWeight,
      amount: order.amount,
      customerType: order.customerType,
      status: order.orderStatus,
      packets: order.packets,
      operatorId: order.operatorId,
      district: order.district,
      createdAt: order.createdAt,
    },
  };

  const questions = {
    isSuspicious: noul("Does this order show signs of potential fraud or risk?", {
      yes: "Unusual patterns: mismatched weights, new high-value customer, rush orders to unfamiliar locations",
      no: "Normal patterns: consistent weights, known customer, standard delivery",
    }),
  };

  try {
    const response = await client.systemOne({ state, questions });
    return {
      noul: response.answers.isSuspicious.noul,
      confidence: response.answers.isSuspicious.confidence,
    };
  } catch (error) {
    console.error("TypeSafe fraud check error:", error);
    return { noul: 0.5, confidence: 0, error: error.message };
  }
};

export { client as typesafeClient };